import { Box, Paper, Typography } from "@mui/material";

interface ViewOnlyInfoCardProps {
  selectedFieldName?: string | null;
  fieldType?: "User input" | "Parameter" | "Formula" | null;
  valueSourceText?: string | null;
  impactText?: string | null;
}

/**
 * View-only version of InfoCard that displays Value Source & Impact
 * without copy buttons (for read-only forecast views)
 */
const ViewOnlyInfoCard = ({
  selectedFieldName,
  fieldType,
  valueSourceText,
  impactText,
}: ViewOnlyInfoCardProps) => {
  const renderFormula = (formula: string) => {
    // Split by the first colon to separate label from formula
    const colonIndex = formula.indexOf(":");

    if (colonIndex === -1) {
      // No colon found, render entire text in secondary color
      return (
        <Typography
          component="div"
          variant="body2"
          sx={{
            color: "#7B1FA2",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: impactText ? "4px" : 0,
          }}
        >
          {formula}
        </Typography>
      );
    }

    const label = formula.substring(0, colonIndex + 1); // Include the colon
    const formulaPart = formula.substring(colonIndex + 1).trim();

    return (
      <Typography
        component="div"
        variant="body2"
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          marginBottom: impactText ? "4px" : 0,
        }}
      >
        <span style={{ color: "rgba(0, 0, 0, 0.87)" }}>{label}</span>{" "}
        <span style={{ color: "#7B1FA2" }}>{formulaPart}</span>
      </Typography>
    );
  };

  const renderImpactText = (text: string) => {
    // Split by colon to find the part after "you will affect:"
    const colonIndex = text.indexOf(":");

    if (colonIndex === -1) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "rgba(0, 0, 0, 0.6)",
            fontSize: "14px",
            lineHeight: "20px",
          }}
        >
          {text}
        </Typography>
      );
    }

    const beforeColon = text.substring(0, colonIndex + 1);
    const afterColon = text.substring(colonIndex + 1);

    // Split the part after colon by commas to identify field names
    const fieldNames = afterColon.split(",").map((name) => name.trim());

    return (
      <Typography
        component="div"
        variant="body2"
        sx={{
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: "14px",
          lineHeight: "20px",
        }}
      >
        {beforeColon}
        {fieldNames.map((field, index) => (
          <span key={index}>
            {index > 0 && ","}{" "}
            <strong style={{ fontWeight: 600 }}>{field}</strong>
          </span>
        ))}
      </Typography>
    );
  };

  // Show value source section if we have a selected field
  const showValueSource = selectedFieldName && (valueSourceText || impactText);

  if (!showValueSource) {
    return (
      <Paper
        elevation={0}
        sx={{
          padding: "16px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          maxWidth: "100%",
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <Typography
            sx={{
              position: "absolute",
              top: "-8px",
              left: "12px",
              backgroundColor: "#fff",
              px: 0.5,
              fontSize: "12px",
              fontWeight: 500,
              color: "rgba(0,0,0,0.6)",
            }}
          >
            Value Source & Impact
          </Typography>

          <Box
            sx={{
              border: "1px solid #000000",
              borderRadius: "4px",
              padding: "16px",
              fontSize: "14px",
              // lineHeight: "20px",
              backgroundColor: "#fff",
              color: "rgba(0,0,0,0.6)",
            }}
          >
            <div>Here you will see how this value was calculated.</div>

            <div style={{ marginTop: "4px" }}>
              Which fields will be affected by editing it.
            </div>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        maxWidth: "100%",
      }}
    >
      <Box sx={{ position: "relative", width: "100%" }}>
        <Typography
          sx={{
            position: "absolute",
            top: "-8px",
            left: "12px",
            backgroundColor: "#fff",
            px: 0.5,
            fontSize: "12px",
            fontWeight: 500,
            color: "rgba(0,0,0,0.6)",
          }}
        >
          Value Source & Impact
        </Typography>
        <Box
          sx={{
            border: "1px solid #000000",
            borderRadius: "4px",
            padding: "12px 16px",
            backgroundColor: "#FFFFFF",
          }}
        >
          {/* First line: Glossary text or Formula */}
          {valueSourceText && (
            <>
              {fieldType === "Formula" ? (
                renderFormula(valueSourceText)
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "14px",
                    lineHeight: "20px",
                    marginBottom: impactText ? "4px" : 0,
                  }}
                >
                  {valueSourceText}
                </Typography>
              )}
            </>
          )}

          {/* Second line: Impact text */}
          {impactText && renderImpactText(impactText)}
        </Box>
      </Box>
    </Paper>
  );
};

export default ViewOnlyInfoCard;
